import ExcelJS from "exceljs";
import { format, getISOWeek } from "date-fns";
import { COMPANY_NAME, COMPANY_RIF } from "./company.js";
import { milkPayStatus, normalizeMilkPayment, sortMilkPayments, splitDayDiscount } from "./milkUtils.js";

const GREEN = "1F6B4A";
const GREEN_SOFT = "E7EFE9";
const GREEN_ROW = "D4EBDD";
const AMBER = "FEF3C7";
const WHITE = "FFFFFF";
const LINE = "D1D5DB";

const FMT_L = "#,##0.00";
const FMT_MONEY = '"$"#,##0.00';
const FMT_PRICE = "#,##0.00##";
const FMT_RATE = "#,##0.00";
const FMT_DATE = "dd/mm/yyyy";

const thin = { style: "thin", color: { argb: LINE } };
const border = { top: thin, left: thin, bottom: thin, right: thin };

const fillSolid = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });

const dateKey = (value) => String(value || "").slice(0, 10);

const n2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const nOrEmpty = (value) => {
  const n = n2(value);
  return n === 0 ? null : n;
};

const colLetter = (index) => {
  let n = index;
  let text = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    text = String.fromCharCode(65 + rem) + text;
    n = Math.floor((n - 1) / 26);
  }
  return text;
};

const payStatusLabel = (provider) => {
  const status = milkPayStatus(provider);
  if (status === "paid") return "Pagado";
  if (status === "pending") return "Pendiente";
  return "Sin movimiento";
};

function styleCell(cell, opts = {}) {
  cell.font = opts.font || { name: "Calibri", size: 10, color: { argb: "1F2937" } };
  if (opts.fill) cell.fill = fillSolid(opts.fill);
  cell.alignment = opts.alignment || { vertical: "middle" };
  cell.border = opts.border === false ? undefined : border;
  if (opts.numFmt) cell.numFmt = opts.numFmt;
}

function headerFont(size = 10) {
  return { name: "Calibri", size, bold: true, color: { argb: WHITE } };
}

function titleFont(size = 16) {
  return { name: "Calibri", size, bold: true, color: { argb: GREEN } };
}

function applyHeaderRow(row, { fill = GREEN, fontSize = 10, wrap = false } = {}) {
  row.height = wrap ? 28 : 20;
  row.eachCell((cell) => {
    styleCell(cell, {
      font: headerFont(fontSize),
      fill,
      alignment: { vertical: "middle", horizontal: "center", wrapText: wrap },
    });
  });
}

function applyBodyRow(row, { fill, bold = false } = {}) {
  row.height = 18;
  row.eachCell((cell) => {
    const current = cell.font || {};
    styleCell(cell, {
      font: { name: "Calibri", size: 10, bold, color: current.color || { argb: "1F2937" } },
      fill,
      alignment: cell.alignment?.horizontal
        ? { vertical: "middle", horizontal: cell.alignment.horizontal }
        : { vertical: "middle" },
      numFmt: cell.numFmt,
    });
  });
}

async function downloadXlsx(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function weekMeta(weekly) {
  const days = weekly?.days || [];
  const start = dateKey(days[0]?.date || weekly?.startOfWeek);
  const end = dateKey(days[6]?.date || weekly?.endOfWeek);
  const startLabel = start ? format(new Date(`${start}T12:00:00`), "dd/MM/yyyy") : "";
  const endLabel = end ? format(new Date(`${end}T12:00:00`), "dd/MM/yyyy") : "";
  const weekNo = end ? getISOWeek(new Date(`${end}T12:00:00`)) : "";
  const generatedAt = format(new Date(), "dd/MM/yyyy HH:mm");
  return {
    days,
    start,
    end,
    weekNo,
    generatedAt,
    periodLabel: `Jueves ${startLabel} — Miércoles ${endLabel}`,
  };
}

function providerDiscountAmount(provider, days) {
  const priceCow = Number(provider.priceCow) || 0;
  const priceBuffalo = Number(provider.priceBuffalo) || 0;
  return days.reduce((sum, day) => {
    const cell = provider.daily?.[day.date] || {};
    const split = splitDayDiscount(cell.vaca, cell.bufalo, cell.discount, cell.discountScope);
    return sum + split.vacaDisc * priceCow + split.bufaloDisc * priceBuffalo;
  }, 0);
}

function dayGross(provider, day) {
  const cell = provider.daily?.[day.date] || {};
  return {
    vaca: Number(cell.vaca) || 0,
    bufalo: Number(cell.bufalo) || 0,
    discount: Number(cell.discount) || 0,
  };
}

function writeBanner(sheet, { subtitle, periodLabel, weekNo, generatedAt, extra }) {
  sheet.getCell("A1").value = COMPANY_NAME;
  sheet.getCell("A1").font = titleFont(16);
  sheet.getCell("A2").value = `RIF ${COMPANY_RIF}`;
  sheet.getCell("A2").font = { name: "Calibri", size: 10, color: { argb: "4B5563" } };
  sheet.getCell("A3").value = subtitle;
  sheet.getCell("A3").font = { name: "Calibri", size: 13, bold: true, color: { argb: GREEN } };
  sheet.getCell("A4").value = weekNo ? `${periodLabel}    Semana ISO ${weekNo}` : periodLabel;
  sheet.getCell("A4").font = { name: "Calibri", size: 10 };
  sheet.getCell("A5").value = extra ? `Generado: ${generatedAt}    ${extra}` : `Generado: ${generatedAt}`;
  sheet.getCell("A5").font = { name: "Calibri", size: 9, color: { argb: "6B7280" } };
}

function setupSheet(sheet, { frozenRows = 8, frozenCols = 1, landscape = true } = {}) {
  sheet.views = [{ state: "frozen", ySplit: frozenRows, xSplit: frozenCols, showGridLines: false }];
  sheet.pageSetup = {
    orientation: landscape ? "landscape" : "portrait",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    paperSize: 9,
    margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
  };
  sheet.headerFooter.oddFooter = `&L${COMPANY_NAME}&C&P / &N&RLeche semanal`;
}

function writeResumen(workbook, { weekly, meta, bcvRate, resumen }) {
  const sheet = workbook.addWorksheet("Resumen", { properties: { tabColor: { argb: GREEN } } });
  sheet.columns = [
    { width: 16 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 14 },
  ];
  const rate = Number(bcvRate?.rate ?? bcvRate) || 0;
  writeBanner(sheet, {
    subtitle: "REPORTE SEMANAL DE LECHE",
    periodLabel: meta.periodLabel,
    weekNo: meta.weekNo,
    generatedAt: meta.generatedAt,
    extra: rate > 0 ? `Tasa BCV: ${rate.toLocaleString("es-VE", { minimumFractionDigits: 2 })}` : "",
  });

  const kpiLabels = sheet.getRow(7);
  ["Total litros", "Vaca (L)", "Búfala (L)", "Desc. agua (L)", "Total $", "Abonado $", "Pendiente $"].forEach(
    (label, i) => {
      kpiLabels.getCell(i + 1).value = label;
    },
  );
  applyHeaderRow(kpiLabels);

  const kpi = sheet.getRow(8);
  const values = [
    { v: n2(resumen.totalLiters), fmt: FMT_L },
    { v: n2(resumen.litersVaca), fmt: FMT_L },
    { v: n2(resumen.litersBufalo), fmt: FMT_L },
    { v: n2(resumen.discountLiters), fmt: FMT_L },
    { v: n2(weekly.totalOwed), fmt: FMT_MONEY },
    { v: n2(weekly.totalPaid), fmt: FMT_MONEY },
    { v: n2(weekly.totalPending), fmt: FMT_MONEY },
  ];
  values.forEach((item, i) => {
    const cell = kpi.getCell(i + 1);
    cell.value = item.v;
    cell.numFmt = item.fmt;
    cell.alignment = { vertical: "middle", horizontal: "center" };
  });
  applyBodyRow(kpi, { fill: GREEN_ROW, bold: true });
  kpi.height = 22;

  sheet.getCell("A10").value = "LITRAJE DIARIO DE LA PLANTA";
  sheet.getCell("A10").font = { name: "Calibri", size: 11, bold: true, color: { argb: GREEN } };

  const dayHeader = sheet.getRow(11);
  dayHeader.getCell(1).value = "Resumen";
  meta.days.forEach((day, i) => {
    const label = day.shortLabel || day.label || "";
    const date = day.date ? format(new Date(`${day.date}T12:00:00`), "dd/MM") : "";
    dayHeader.getCell(i + 2).value = date ? `${label} ${date}` : label;
  });
  dayHeader.getCell(9).value = "Total L";
  dayHeader.getCell(10).value = "Precio";
  dayHeader.getCell(11).value = "$";
  applyHeaderRow(dayHeader, { wrap: true });

  const rows = [
    {
      label: "Vaca",
      daily: resumen.dailyVaca,
      total: resumen.litersVaca,
      price: resumen.priceCow,
      amount: resumen.owedVaca,
    },
    {
      label: "Búfala",
      daily: resumen.dailyBufalo,
      total: resumen.litersBufalo,
      price: resumen.priceBuffalo,
      amount: resumen.owedBufalo,
      fill: "F8FAFC",
    },
    {
      label: "Descuento",
      daily: resumen.dailyDiscount,
      total: resumen.discountLiters,
      price: "Agua",
      amount: -n2(resumen.discountAmount),
      fill: AMBER,
    },
    {
      label: "Total L",
      daily: Object.fromEntries(
        meta.days.map((day) => [
          day.date,
          (Number(resumen.dailyVaca[day.date]) || 0) + (Number(resumen.dailyBufalo[day.date]) || 0),
        ]),
      ),
      total: resumen.totalLiters,
      price: "Total $",
      amount: n2(weekly.totalOwed),
      fill: GREEN_SOFT,
      bold: true,
    },
  ];

  rows.forEach((item, index) => {
    const row = sheet.getRow(12 + index);
    row.getCell(1).value = item.label;
    meta.days.forEach((day, i) => {
      const cell = row.getCell(i + 2);
      cell.value = n2(item.daily?.[day.date]);
      cell.numFmt = FMT_L;
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });
    row.getCell(9).value = n2(item.total);
    row.getCell(9).numFmt = FMT_L;
    if (typeof item.price === "number") {
      row.getCell(10).value = n2(item.price);
      row.getCell(10).numFmt = FMT_PRICE;
    } else {
      row.getCell(10).value = item.price;
    }
    row.getCell(11).value = n2(item.amount);
    row.getCell(11).numFmt = FMT_MONEY;
    applyBodyRow(row, { fill: item.fill, bold: item.bold });
  });

  sheet.getCell("A17").value = `Productores con litraje: ${resumen.activeCount}    Abonos registrados: ${resumen.paymentCount}`;
  sheet.getCell("A17").font = { name: "Calibri", size: 9, color: { argb: "6B7280" } };

  setupSheet(sheet, { frozenRows: 11, frozenCols: 1 });
}

function writeProductores(workbook, { weekly, meta }) {
  const sheet = workbook.addWorksheet("Productores", { properties: { tabColor: { argb: "0F766E" } } });
  const headers = [
    "Productor",
    "Vaca (L)",
    "Búfala (L)",
    "Desc. (L)",
    "Total (L)",
    "$/L Vaca",
    "$/L Búfala",
    "Desc. $",
    "Total $",
    "Abonado $",
    "Pendiente $",
    "Estado",
  ];
  sheet.columns = [
    { width: 28 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 13 },
    { width: 13 },
    { width: 14 },
    { width: 16 },
  ];
  writeBanner(sheet, {
    subtitle: "PRODUCTORES — LITRAJE, DEUDA Y ABONOS",
    periodLabel: meta.periodLabel,
    weekNo: meta.weekNo,
    generatedAt: meta.generatedAt,
  });

  const header = sheet.getRow(7);
  headers.forEach((label, i) => {
    header.getCell(i + 1).value = label;
  });
  applyHeaderRow(header);

  const providers = weekly.providerTotals || [];
  providers.forEach((provider, index) => {
    const rowNumber = 8 + index;
    const row = sheet.getRow(rowNumber);
    const discountLiters = n2(provider.discountLiters);
    const discountUsd = n2(providerDiscountAmount(provider, meta.days));
    const litersVaca = n2(provider.litersVaca);
    const litersBufalo = n2(provider.litersBufalo);
    const totalOwed = n2(provider.totalOwed);
    const totalPaid = n2(provider.totalPaid);
    const status = payStatusLabel(provider);

    row.getCell(1).value = provider.name || "";
    row.getCell(2).value = litersVaca;
    row.getCell(3).value = litersBufalo;
    row.getCell(4).value = discountLiters;
    row.getCell(5).value = { formula: `B${rowNumber}+C${rowNumber}` };
    row.getCell(6).value = n2(provider.priceCow);
    row.getCell(7).value = n2(provider.priceBuffalo);
    row.getCell(8).value = discountUsd;
    row.getCell(9).value = totalOwed;
    row.getCell(10).value = totalPaid;
    row.getCell(11).value = { formula: `I${rowNumber}-J${rowNumber}` };
    row.getCell(12).value = status;

    [2, 3, 4, 5].forEach((col) => {
      row.getCell(col).numFmt = FMT_L;
    });
    [6, 7].forEach((col) => {
      row.getCell(col).numFmt = FMT_PRICE;
    });
    [8, 9, 10, 11].forEach((col) => {
      row.getCell(col).numFmt = FMT_MONEY;
    });
    [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].forEach((col) => {
      row.getCell(col).alignment = { horizontal: "right", vertical: "middle" };
    });

    let fill = index % 2 === 0 ? WHITE : "F8FAFC";
    if (status === "Pagado") fill = "ECFDF5";
    else if (status === "Pendiente") fill = "FFFBEB";
    applyBodyRow(row, { fill });
  });

  const first = 8;
  const last = 7 + providers.length;
  if (providers.length > 0) {
    const total = sheet.getRow(last + 1);
    total.getCell(1).value = "TOTALES";
    ["B", "C", "D", "E", "H", "I", "J", "K"].forEach((letter) => {
      const cell = total.getCell(letter);
      cell.value = { formula: `SUM(${letter}${first}:${letter}${last})` };
    });
    ["B", "C", "D", "E"].forEach((letter) => {
      total.getCell(letter).numFmt = FMT_L;
    });
    ["H", "I", "J", "K"].forEach((letter) => {
      total.getCell(letter).numFmt = FMT_MONEY;
    });
    applyBodyRow(total, { fill: GREEN_ROW, bold: true });
    sheet.autoFilter = { from: { row: 7, column: 1 }, to: { row: last, column: 12 } };
  }

  setupSheet(sheet, { frozenRows: 7, frozenCols: 1 });
}

function writeLitrajes(workbook, { weekly, meta }) {
  const sheet = workbook.addWorksheet("Litrajes diarios", { properties: { tabColor: { argb: "B45309" } } });
  const providers = (weekly.providerTotals || []).filter(
    (p) => n2(p.litersVaca) + n2(p.litersBufalo) + n2(p.discountLiters) > 0,
  );
  const dayCount = meta.days.length;
  const firstDayCol = 2;
  const totVacaCol = firstDayCol + dayCount * 3;
  const totBufaloCol = totVacaCol + 1;
  const totDescCol = totVacaCol + 2;
  const totLCol = totVacaCol + 3;
  const lastCol = totLCol;

  sheet.getColumn(1).width = 28;
  for (let col = 2; col <= lastCol; col += 1) sheet.getColumn(col).width = 11;

  writeBanner(sheet, {
    subtitle: "LITRAJES RECIBIDOS POR PRODUCTOR Y DÍA",
    periodLabel: meta.periodLabel,
    weekNo: meta.weekNo,
    generatedAt: meta.generatedAt,
  });

  const top = sheet.getRow(7);
  const sub = sheet.getRow(8);
  top.getCell(1).value = "Productor";
  sheet.mergeCells(7, 1, 8, 1);
  meta.days.forEach((day, i) => {
    const startCol = firstDayCol + i * 3;
    const label = day.shortLabel || day.label || "";
    const date = day.date ? format(new Date(`${day.date}T12:00:00`), "dd/MM") : "";
    top.getCell(startCol).value = date ? `${label} ${date}` : label;
    sheet.mergeCells(7, startCol, 7, startCol + 2);
    sub.getCell(startCol).value = "Vaca";
    sub.getCell(startCol + 1).value = "Búfala";
    sub.getCell(startCol + 2).value = "Desc.";
  });
  top.getCell(totVacaCol).value = "Totales";
  sheet.mergeCells(7, totVacaCol, 7, totLCol);
  sub.getCell(totVacaCol).value = "Vaca";
  sub.getCell(totBufaloCol).value = "Búfala";
  sub.getCell(totDescCol).value = "Desc.";
  sub.getCell(totLCol).value = "Neto L";
  top.height = 28;
  sub.height = 20;
  for (let col = 1; col <= lastCol; col += 1) {
    styleCell(top.getCell(col), {
      font: headerFont(10),
      fill: GREEN,
      alignment: { vertical: "middle", horizontal: "center", wrapText: true },
    });
    styleCell(sub.getCell(col), {
      font: headerFont(10),
      fill: "2F6B4F",
      alignment: { vertical: "middle", horizontal: "center" },
    });
  }

  const vacaCols = meta.days.map((_, i) => colLetter(firstDayCol + i * 3));
  const bufaloCols = meta.days.map((_, i) => colLetter(firstDayCol + i * 3 + 1));
  const descCols = meta.days.map((_, i) => colLetter(firstDayCol + i * 3 + 2));

  providers.forEach((provider, index) => {
    const rowNumber = 9 + index;
    const row = sheet.getRow(rowNumber);
    row.getCell(1).value = provider.name || "";
    meta.days.forEach((day, i) => {
      const { vaca, bufalo, discount } = dayGross(provider, day);
      const startCol = firstDayCol + i * 3;
      row.getCell(startCol).value = nOrEmpty(vaca);
      row.getCell(startCol + 1).value = nOrEmpty(bufalo);
      row.getCell(startCol + 2).value = nOrEmpty(discount);
      [startCol, startCol + 1, startCol + 2].forEach((col) => {
        row.getCell(col).numFmt = FMT_L;
        row.getCell(col).alignment = { horizontal: "center", vertical: "middle" };
      });
    });
    row.getCell(totVacaCol).value = { formula: `SUM(${vacaCols.map((c) => `${c}${rowNumber}`).join(",")})` };
    row.getCell(totBufaloCol).value = { formula: `SUM(${bufaloCols.map((c) => `${c}${rowNumber}`).join(",")})` };
    row.getCell(totDescCol).value = { formula: `SUM(${descCols.map((c) => `${c}${rowNumber}`).join(",")})` };
    row.getCell(totLCol).value = {
      formula: `${colLetter(totVacaCol)}${rowNumber}+${colLetter(totBufaloCol)}${rowNumber}-${colLetter(totDescCol)}${rowNumber}`,
    };
    [totVacaCol, totBufaloCol, totDescCol, totLCol].forEach((col) => {
      row.getCell(col).numFmt = FMT_L;
      row.getCell(col).alignment = { horizontal: "right", vertical: "middle" };
    });
    applyBodyRow(row, { fill: index % 2 === 0 ? WHITE : "F8FAFC" });
  });

  if (providers.length > 0) {
    const first = 9;
    const last = 8 + providers.length;
    const total = sheet.getRow(last + 1);
    total.getCell(1).value = "TOTAL RECIBIDO";
    for (let col = 2; col <= lastCol; col += 1) {
      const letter = colLetter(col);
      total.getCell(col).value = { formula: `SUM(${letter}${first}:${letter}${last})` };
      total.getCell(col).numFmt = FMT_L;
    }
    applyBodyRow(total, { fill: GREEN_ROW, bold: true });
    sheet.autoFilter = { from: { row: 8, column: 1 }, to: { row: last, column: lastCol } };
  } else {
    sheet.getCell("A9").value = "No hay litrajes registrados en esta semana.";
  }

  setupSheet(sheet, { frozenRows: 8, frozenCols: 1 });
}

function writeAbonos(workbook, { weekly, meta, bcvRate }) {
  const sheet = workbook.addWorksheet("Abonos", { properties: { tabColor: { argb: "1D4ED8" } } });
  sheet.columns = [
    { width: 14 },
    { width: 28 },
    { width: 18 },
    { width: 18 },
    { width: 12 },
    { width: 14 },
    { width: 12 },
    { width: 13 },
    { width: 14 },
    { width: 28 },
  ];
  writeBanner(sheet, {
    subtitle: "ABONOS DE LA SEMANA",
    periodLabel: meta.periodLabel,
    weekNo: meta.weekNo,
    generatedAt: meta.generatedAt,
  });

  const header = sheet.getRow(7);
  ["Fecha", "Productor", "Método", "Referencia", "Moneda", "Monto orig.", "Tasa", "Monto $", "Monto Bs", "Notas"].forEach(
    (label, i) => {
      header.getCell(i + 1).value = label;
    },
  );
  applyHeaderRow(header);

  const fallbackRate = Number(bcvRate?.rate ?? bcvRate) || 0;
  const payments = sortMilkPayments(weekly.payments || []);
  payments.forEach((pay, index) => {
    const row = sheet.getRow(8 + index);
    const normalized = normalizeMilkPayment(pay, fallbackRate);
    const providerName = pay.provider?.name || "";
    const method = pay.paymentMethod?.name || pay.method || "";
    const currency = String(pay.currency || "USD").toUpperCase();
    const rawDate = normalized.date;
    const dateValue =
      dateKey(rawDate).length >= 10 ? new Date(`${dateKey(rawDate)}T12:00:00`) : rawDate ? new Date(rawDate) : null;

    row.getCell(1).value = dateValue && !Number.isNaN(dateValue.getTime()) ? dateValue : null;
    row.getCell(1).numFmt = FMT_DATE;
    row.getCell(2).value = providerName;
    row.getCell(3).value = method;
    row.getCell(4).value = pay.reference || "";
    row.getCell(5).value = currency;
    row.getCell(6).value = n2(pay.amount);
    row.getCell(6).numFmt = currency === "BS" ? "#,##0.00" : FMT_MONEY;
    row.getCell(7).value = nOrEmpty(normalized.rate);
    row.getCell(7).numFmt = FMT_RATE;
    row.getCell(8).value = n2(normalized.usd);
    row.getCell(8).numFmt = FMT_MONEY;
    row.getCell(9).value = nOrEmpty(normalized.bs);
    row.getCell(9).numFmt = "#,##0.00";
    row.getCell(10).value = pay.notes || "";
    applyBodyRow(row, { fill: index % 2 === 0 ? WHITE : "F8FAFC" });
  });

  if (payments.length > 0) {
    const first = 8;
    const last = 7 + payments.length;
    const total = sheet.getRow(last + 1);
    total.getCell(1).value = "TOTAL ABONADO";
    total.getCell(8).value = { formula: `SUM(H${first}:H${last})` };
    total.getCell(8).numFmt = FMT_MONEY;
    total.getCell(9).value = { formula: `SUM(I${first}:I${last})` };
    total.getCell(9).numFmt = "#,##0.00";
    applyBodyRow(total, { fill: GREEN_ROW, bold: true });
    sheet.autoFilter = { from: { row: 7, column: 1 }, to: { row: last, column: 10 } };
  } else {
    sheet.getCell("A8").value = "No hay abonos registrados en esta semana.";
  }

  setupSheet(sheet, { frozenRows: 7, frozenCols: 2 });
}

export async function exportMilkWeekExcel({ weekly, bcvRate, resumen }) {
  if (!weekly) throw new Error("No hay datos de la semana para exportar.");
  const meta = weekMeta(weekly);
  const providers = weekly.providerTotals || [];
  const payments = weekly.payments || [];
  const computed = resumen || {
    dailyVaca: {},
    dailyBufalo: {},
    dailyDiscount: {},
    litersVaca: 0,
    litersBufalo: 0,
    discountLiters: 0,
    discountAmount: 0,
    owedVaca: 0,
    owedBufalo: 0,
    priceCow: 0,
    priceBuffalo: 0,
    totalLiters: 0,
  };

  const workbook = new ExcelJS.Workbook();
  workbook.creator = COMPANY_NAME;
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.title = `Leche semanal ${meta.periodLabel}`;

  writeResumen(workbook, {
    weekly,
    meta,
    bcvRate,
    resumen: {
      ...computed,
      activeCount: providers.filter((p) => n2(p.totalLiters) > 0).length,
      paymentCount: payments.length,
    },
  });
  writeProductores(workbook, { weekly, meta });
  writeLitrajes(workbook, { weekly, meta });
  writeAbonos(workbook, { weekly, meta, bcvRate });

  await downloadXlsx(workbook, `leche_semana_${meta.start || "reporte"}.xlsx`);
}
